import React, { useMemo, useState } from "react";
import "@/assets/css/DocumentSession.scss";
import {
  ChevronLeft,
  FileText,
  Sparkles,
  Folder as FolderIcon,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  Loader2,
  Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDocumentsData, type DocumentItem } from "./documentsData";
import DocxViewer from "../ui/DocxViewer";

declare namespace JSX {
  interface IntrinsicElements {
    "lottie-player": any;
  }
}

export default function DocumentSession({
  docs,
  initialId,
  onBack,
  setCurrentDocumentScreen,
}: {
  docs: DocumentItem[];
  initialId: string;
  onBack: () => void;
  setCurrentDocumentScreen: (screen: string) => void;
}) {
  const [activeId, setActiveId] = useState<string>(initialId);
  const [query, setQuery] = useState("");
  const dataHook = useDocumentsData() || ({} as any);
  const {
    viewingDoc,
    updateDocumentcomplienceStatus,
    handleTreeNodesClick,
    updateDocumentAssignments,
    currentUserEmail,
    refreshdocuments,
    refreshDocumentQuizData,
    companyUserDocuments
  } = dataHook;

  const activeDoc = useMemo(() => {
    if (companyUserDocuments && companyUserDocuments.length > 0) {
      return (
        companyUserDocuments.find((d) => d._id === activeId) || companyUserDocuments[0]
      );
    }
    return docs.find((d) => d._id === activeId || d.id === activeId) || docs[0];
  }, [companyUserDocuments, activeId, docs]);
  // Refresh documents when component mounts to ensure latest data
  React.useEffect(() => {
    if (!companyUserDocuments || companyUserDocuments.length === 0) {
      refreshdocuments?.();
    }
    // Only run on mount to avoid infinite refresh cycles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  type TreeNode = {
    key: string;
    name: string;
    children: Map<string, TreeNode>;
    docs: DocumentItem[];
  };
  const buildTree = (list: DocumentItem[]): TreeNode => {
    const root: TreeNode = {
      key: "root",
      name: "root",
      children: new Map(),
      docs: [],
    };
    const validList = Array.isArray(list) ? list : [];
    for (const d of validList) {
      const path =
        d.folder && d.folder.trim() ? d.folder.split(/\s*\/\s*/g) : [d.type];
      let node = root;
      for (const part of path) {
        const key = `${node.key}/${part}`;
        if (!node.children.has(part))
          node.children.set(part, {
            key,
            name: part,
            children: new Map(),
            docs: [],
          });
        node = node.children.get(part)!;
      }
      node.docs.push(d);
    }
    return root;
  };
  const getCount = (node: TreeNode): number => {
    let count = node.docs.length;
    node.children.forEach((child) => {
      count += getCount(child);
    });
    return count;
  };
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const ensureExpandedPath = (list: DocumentItem[], id: string) => {
    const found = list.find((d) => d.id === id);
    if (!found) return {} as Record<string, boolean>;
    const parts =
      found.folder && found.folder.trim()
        ? found.folder.split(/\s*\/\s*/g)
        : [found.type];
    const state: Record<string, boolean> = {};
    let key = "root";
    for (const p of parts) {
      key = `${key}/${p}`;
      state[key] = true;
    }
    return state;
  };
  // Use companyDocuments from hook, fallback to docs prop
  const documentList =
    companyUserDocuments && companyUserDocuments.length > 0 ? companyUserDocuments : docs;

  React.useEffect(() => {
    setExpanded((prev) => ({
      ...ensureExpandedPath(documentList, activeId),
      ...prev,
    }));
  }, [activeId, documentList]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return documentList;
    return documentList.filter(
      (d) =>
        d.title.toLowerCase().includes(q) ||
        d.type.toLowerCase().includes(q) ||
        (d.folder || "").toLowerCase().includes(q)
    );
  }, [documentList, query]);

  const [readStatus, setReadStatus] = useState<
    Record<string, "idle" | "animating" | "done">
  >({});
  const [readAt, setReadAt] = useState<Record<string, string>>({});

  // Quiz state per document
  const [answersByDoc, setAnswersByDoc] = useState<
    Record<string, Record<string, number | null>>
  >({});
  const [quizResult, setQuizResult] = useState<
    Record<
      string,
      { score: number; total: number; per: Record<string, boolean> }
    >
  >({});
  const [quizCompletedAt, setQuizCompletedAt] = useState<
    Record<string, string>
  >({});
  const [quizStarted, setQuizStarted] = useState<Record<string, boolean>>({});
  const [quizLoadingState, setQuizLoadingState] = useState<
    Record<string, boolean>
  >({});
  const [quizCelebrateDone, setQuizCelebrateDone] = useState<
    Record<string, boolean>
  >({});

  // After marking quiz as complete, show lottie then switch to certificate panel
  React.useEffect(() => {
    const ids = Object.keys(quizCompletedAt || {});
    if (!ids.length) return;
    const lastId = ids[ids.length - 1];
    if (!quizCompletedAt[lastId] || quizCelebrateDone[lastId]) return;
    const t = window.setTimeout(
      () => setQuizCelebrateDone((p) => ({ ...p, [lastId]: true })),
      2600
    );
    return () => window.clearTimeout(t);
  }, [quizCompletedAt]);

  // Load Lottie web component once (for quiz celebration)
  React.useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const win = window as any;
      // If already ready or loading, bail (helps React StrictMode double-invoke)
      if (
        win.__lottie_player_loaded__ === true ||
        win.__lottie_player_loaded__ === "loading"
      )
        return;
      // If custom element already defined or script tag already present, mark ready/loading accordingly
      if (win.customElements?.get?.("lottie-player")) {
        win.__lottie_player_loaded__ = true;
        return;
      }
      if (document.head.querySelector('script[data-lottie-player="1"]')) {
        win.__lottie_player_loaded__ = "loading";
        return;
      }
      win.__lottie_player_loaded__ = "loading";
      const attach = (src: string, onFail?: () => void) => {
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.crossOrigin = "anonymous";
        s.referrerPolicy = "no-referrer";
        s.setAttribute("data-lottie-player", "1");
        s.onload = () => (win.__lottie_player_loaded__ = true);
        s.onerror = () => {
          if (onFail) onFail();
          else win.__lottie_player_loaded__ = "failed";
        };
        document.head.appendChild(s);
      };
      // Try jsdelivr first, then unpkg
      attach(
        "https://cdn.jsdelivr.net/npm/@lottiefiles/lottie-player@latest/dist/lottie-player.js",
        () =>
          attach(
            "https://unpkg.com/@lottiefiles/lottie-player@latest/dist/lottie-player.js"
          )
      );
    } catch (e) {
      // no-op
    }
  }, []);

  const handleBackClick = () => {
    setCurrentDocumentScreen("Documents");
  };

  const hasQuiz = useMemo(() => {
    if (!activeDoc) return false;

    return activeDoc.quiz?.questions?.length > 0;
  }, [activeDoc]);

  const isMarkedAsRead = useMemo(() => {
    if (!activeDoc || !currentUserEmail) return false;

    const userAssignment = activeDoc.assignments?.find(
      (a) => a.email === currentUserEmail
    );

    const isRead = userAssignment?.markAsRead === true;

    return isRead;
  }, [activeDoc, currentUserEmail]);

  // Check if quiz is already performed AND passed by current user
  const isQuizPerformed = useMemo(() => {
    if (!activeDoc || !currentUserEmail) return false;

    const userAssignment = activeDoc.assignments?.find(
      (a) => a.email === currentUserEmail
    );

    const totalQuestions = activeDoc.quiz?.questions?.length || 0;
    const userScore = userAssignment?.quizScore || 0;

    // Only consider quiz "performed" if user got all questions correct
    const isCompleted =
      userAssignment?.quizCompleted === true && userScore === totalQuestions;

    return isCompleted;
  }, [activeDoc, currentUserEmail]);

  const handleSubmitQuiz = async () => {
    if (!activeDoc) return;

    const docId = activeDoc._id;
    const answers = answersByDoc[docId] || {};
    const questions = activeDoc.quiz?.questions || [];
    if (questions.length === 0) {
      console.error("No questions found for quiz");
      return;
    }
    const total = questions.length;
    let correct = 0;
    const per: Record<string, boolean> = {};
    questions.forEach((q: any) => {
      const questionId = q.id || q.QuestionId;
      const ok = answers[questionId] === q.correctIndex;
      per[questionId] = !!ok;
      if (ok) correct++;
    });
    setQuizResult((prev) => ({
      ...prev,
      [docId]: { score: correct, total, per },
    }));
    if (correct === total) {
      const payload = {
        _id: docId,
        Compliant: true,
        QuizScore: correct,
      };
      await updateDocumentAssignments(payload);
      await refreshdocuments();
      setQuizCompletedAt((p) => ({
        ...p,
        [docId]: new Date().toISOString(),
      }));
    }
  };
  // Get user assignment for current user
  const userAssignment = useMemo(() => {
    if (!activeDoc || !currentUserEmail) return null;
    return activeDoc.assignments?.find((a) => a.email === currentUserEmail);
  }, [activeDoc, currentUserEmail]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex items-center gap-2 px-4 py-2 border-b">
        <Button variant="ghost" size="sm" onClick={() => handleBackClick()}>
          <ChevronLeft className="w-4 h-4" /> Back
        </Button>
        <div className="ml-2 text-sm text-gray-600">
          Document Training Session
        </div>
      </div>

      <div className="grid flex-1 min-h-0 grid-cols-12">
        {/* Left: Library */}
        <div className="flex flex-col min-h-0 col-span-3 border-r bg-slate-50">
          <div className="p-3 border-b">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search in library"
              className="h-8 bg-white rounded-md"
            />
          </div>
          <div className="flex-1 overflow-auto px-2.5 py-2">
            {(() => {
              const tree = buildTree(filtered);
              const renderNode = (node: TreeNode, depth = 0) => {
                const rows: React.ReactNode[] = [];
                node.children.forEach((child, name) => {
                  const isOpen = !!expanded[child.key];
                  const isTop = depth === 0;
                  rows.push(
                    <div key={child.key} className="select-none">
                      <div
                        className={`group flex items-center gap-2 px-2.5 py-1.5 rounded-md cursor-pointer hover:bg-indigo-50/40 ${isTop
                            ? "font-semibold text-slate-800"
                            : "text-slate-700"
                          }`}
                        style={{ paddingLeft: depth * 12 + 6 }}
                        onClick={() => {
                          setExpanded((prev) => ({
                            ...prev,
                            [child.key]: !isOpen,
                          }));
                        }}
                      >
                        {isOpen ? (
                          <ChevronDown className="w-4 h-4 text-slate-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-500" />
                        )}
                        {isOpen ? (
                          <FolderOpen className="w-4 h-4 text-indigo-600" />
                        ) : (
                          <FolderIcon className="w-4 h-4 text-indigo-600" />
                        )}
                        <span className="text-sm truncate">{name}</span>
                        <span className="ml-auto inline-flex items-center justify-center text-[10px] leading-4 px-1.5 h-4 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                          {getCount(child)}
                        </span>
                      </div>
                      {isOpen && (
                        <div className="mt-1">
                          {renderNode(child, depth + 1)}
                        </div>
                      )}
                    </div>
                  );
                });
                // Docs at this level
                node.docs.forEach((d, idx) => {
                  const isActive = activeDoc?.id === d.id;
                  rows.push(
                    <div
                      key={d.id}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md cursor-pointer ${isActive
                          ? "bg-indigo-50 ring-1 ring-indigo-200 border border-indigo-200"
                          : "hover:bg-slate-50"
                        }`}
                      style={{ paddingLeft: depth * 12 + 30 }}
                      onClick={async () => {
                        setActiveId(d.id);
                        await handleTreeNodesClick(d);
                      }}
                    >
                      <span className="w-5 text-xs text-right text-slate-400">
                        {idx + 1}
                      </span>
                      <FileText
                        className={`w-4 h-4 ${isActive ? "text-indigo-600" : "text-indigo-500"
                          }`}
                      />
                      <div className="text-sm truncate text-slate-800">
                        {d.title}
                      </div>
                    </div>
                  );
                });
                return rows;
              };
              return <div className="text-sm">{renderNode(tree, 0)}</div>;
            })()}
          </div>
        </div>

        {/* Middle: Document */}
        <div className="flex flex-col min-h-0 col-span-6 bg-slate-50">
          <div className="flex-1 min-h-0 px-2 py-2 overflow-auto">
            <div className="w-full max-w-full mx-auto">
              <div className="overflow-hidden bg-white border rounded-lg shadow-sm">
                <div className="px-4 py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base font-semibold text-gray-900">
                        {activeDoc?.title}
                      </h2>
                      <div className="text-xs text-gray-500">
                        Type: {activeDoc?.type} • Created:{" "}
                        {activeDoc
                          ? new Date(activeDoc.createdAt).toLocaleString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                          : ""}
                      </div>
                    </div>

                    {/* Mark as Read Section - Moved here */}
                    {(() => {
                      const actualReadStatus = isMarkedAsRead
                        ? "done"
                        : readStatus[activeDoc?._id || ""] || "idle";
                      const actualReadAt =
                        userAssignment?.completedAt || readAt[activeDoc?._id || ""];

                      const formatted = actualReadAt
                        ? new Date(actualReadAt).toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })
                        : "";

                      return (
                        <div className="flex-shrink-0">
                          {actualReadStatus === "done" ? (
                            <div className="flex items-center gap-2 px-3 py-2 text-green-700 border border-green-200 rounded-md bg-green-50">
                              <CheckCircle2 className="w-4 h-4" />
                              <div className="text-xs">
                                <div className="font-medium">Marked as read</div>
                                {formatted && (
                                  <div className="text-green-600">{formatted}</div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => {
                                if (actualReadStatus !== "idle") return;
                                setReadStatus((prev) => ({
                                  ...prev,
                                  [activeDoc!.id]: "animating",
                                }));
                                updateDocumentcomplienceStatus({ _id: activeDoc._id })
                                  .then((res) => {
                                    if (res) {
                                      setReadStatus((prev) => ({
                                        ...prev,
                                        [activeDoc!.id]: "done",
                                      }));
                                      setReadAt((prev) => ({
                                        ...prev,
                                        [activeDoc!.id]: new Date().toISOString(),
                                      }));
                                      refreshdocuments();
                                    }
                                  })
                                  .catch(() => {
                                    setReadStatus((prev) => ({
                                      ...prev,
                                      [activeDoc!.id]: "idle",
                                    }));
                                  });
                              }}
                              disabled={actualReadStatus !== "idle"}
                              className="whitespace-nowrap"
                            >
                              {actualReadStatus === "animating" ? (
                                <span className="inline-flex items-center gap-2">
                                  <Loader2 className="w-4 h-4 animate-spin" /> Marking…
                                </span>
                              ) : (
                                "Mark as Read"
                              )}
                            </Button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="border-t">
                  {(() => {
                    const viewing = viewingDoc as
                      | { url?: string; title?: string; mimeType?: string }
                      | undefined;
                    const url = viewing?.url;
                    const title =
                      viewing?.title || activeDoc?.title || "Document";

                    if (!url) {
                      return (
                        <div className="p-6 text-gray-500 text-center">
                          No document to display.
                        </div>
                      );
                    }
                    if (url.startsWith("blob:")) {
                      const maybeMime =
                        viewing?.mimeType ||
                        activeDoc?.mainFile?.mimeType ||
                        "";

                      if (
                        maybeMime.includes(
                          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        ) ||
                        maybeMime.includes("application/msword")
                      ) {
                        return (
                          <div className="h-[85vh] overflow-auto bg-white">
                            <DocxViewer blobUrl={url} className="min-h-full" />
                          </div>
                        );
                      }

                      if (
                        maybeMime.includes("application/vnd.ms-excel") ||
                        maybeMime.includes(
                          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        ) ||
                        maybeMime.includes("application/zip")
                      ) {
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = title;
                        a.click();
                        URL.revokeObjectURL(url);
                        return (
                          <div className="p-6 text-sm text-gray-500 text-center">
                            Downloading file...
                          </div>
                        );
                      }
                      return (
                        <iframe
                          key={url}
                          src={url}
                          title={title}
                          className="w-full h-[85vh] border-0"
                        />
                      );
                    }
                    if (url.startsWith("data:")) {
                      // PDF
                      if (/^data:application\/pdf/i.test(url)) {
                        return (
                          <iframe
                            key={url}
                            src={url}
                            title={title}
                            className="w-full h-[85vh] border-0"
                          />
                        );
                      }
                      if (/^data:(text\/plain|text\/html|text\/)/i.test(url)) {
                        const base64 = url.split(",")[1] || "";
                        let decoded = "";
                        try {
                          decoded = atob(base64);
                        } catch {
                          decoded = "Unable to decode content";
                        }
                        return (
                          <div className="p-6">
                            <div className="text-sm leading-relaxed prose whitespace-pre-wrap max-w-none overflow-y-auto max-h-[70vh]">
                              {decoded || "No content"}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <iframe
                          key={url}
                          src={url}
                          title={title}
                          className="w-full h-[85vh] border-0"
                        />
                      );
                    }
                    if (/\.pdf(\?|$)/i.test(url)) {
                      return (
                        <iframe
                          key={url}
                          src={url}
                          title={title}
                          className="w-full h-[85vh] border-0"
                        />
                      );
                    }
                    return (
                      <div className="p-6 text-center text-sm text-gray-500">
                        This file type can’t be displayed. Please download it
                        instead.
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          
        </div>

        {/* Right: Quiz */}
        <div className="flex flex-col min-h-0 col-span-3 bg-white border-l">
          <div className="flex-1 p-4 overflow-auto">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold">Quiz</h3>
            </div>

            {/* Show quiz performed message if quiz is already completed by current user */}
            {isQuizPerformed ? (
              <div className="border rounded-lg bg-green-50 border-green-200">
                <div className="flex items-start gap-3 p-4">
                  <div className="flex items-center justify-center text-green-700 bg-green-100 rounded-full h-9 w-9">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-green-800">
                      Quiz Completed
                    </div>
                    <div className="mb-3 text-sm text-green-700">
                      You have already completed this quiz. Your score has been
                      recorded.
                    </div>
                    {userAssignment && (
                      <div className="text-xs text-green-600">
                        Score: {userAssignment.quizScore || 0}/
                        {activeDoc?.quiz?.questions?.length}
                        {userAssignment.completedAt && (
                          <span className="ml-2">
                            • Completed on:{" "}
                            {new Date(
                              userAssignment.completedAt
                            ).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : hasQuiz ? (
              (() => {
                const docId = activeDoc!.id;
                const answers = answersByDoc[docId] || {};
                const result = quizResult[docId];
                const completedAt = quizCompletedAt[docId];
                const started = !!quizStarted[docId];
                const readDone = isMarkedAsRead || readStatus[activeDoc?._id || ""] === "done" || readStatus[activeDoc?.id || ""] === "done";

                // Get questions from any of the possible locations
                const questions = activeDoc!.quiz?.questions || [];

                if (!started && !completedAt) {
                  return (
                    <div className="border rounded-lg bg-slate-50">
                      <div className="flex items-start gap-3 p-4">
                        <div className="flex items-center justify-center text-purple-700 bg-purple-100 rounded-full h-9 w-9">
                          <Sparkles className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">
                            There is a quiz for this document
                          </div>
                          <div className="mb-3 text-sm text-slate-600">
                            Click below to start the quiz now. You must read the
                            document first.
                          </div>
                          <Button
                            size="sm"
                            disabled={!readDone || quizLoadingState[docId]}
                            onClick={async () => {
                              // Set loading state
                              setQuizLoadingState((p) => ({
                                ...p,
                                [docId]: true,
                              }));
                              try {
                                // Refresh quiz data before starting quiz
                                await refreshDocumentQuizData?.(docId);
                                setQuizStarted((p) => ({
                                  ...p,
                                  [docId]: true,
                                }));
                              } finally {
                                // Clear loading state
                                setQuizLoadingState((p) => ({
                                  ...p,
                                  [docId]: false,
                                }));
                              }
                            }}
                          >
                            {quizLoadingState[docId] ? (
                              <span className="inline-flex items-center gap-2">
                                <svg
                                  className="w-4 h-4 animate-spin"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                Starting...
                              </span>
                            ) : readDone ? (
                              "Start Quiz"
                            ) : (
                              "Mark document as Read to start"
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                }
                if (completedAt) {
                  const formatted = new Date(completedAt).toLocaleString(
                    "en-GB",
                    {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    }
                  );
                  const state =
                    typeof window !== "undefined"
                      ? (window as any).__lottie_player_loaded__
                      : undefined;
                  const lottieReady = state === true;
                  const docId = activeDoc!.id;
                  if (!quizCelebrateDone[docId]) {
                    return (
                      <div className="flex flex-col items-center justify-center p-4">
                        <div className="w-full">
                          {lottieReady ? (
                            <lottie-player
                              src="https://cdn.builder.io/o/assets%2F0407d02f319145f582bec36d8e4031e2%2F1fb4591c7ec2490db931db5b39b8e361?alt=media&token=87f9e3ae-bc6a-48e4-adf5-9c854c12dd3b&apiKey=0407d02f319145f582bec36d8e4031e2"
                              background="transparent"
                              autoplay
                              style={{ width: "100%", height: "80vh" }}
                            />
                          ) : (
                            <div className="h-[70vh] md:h-[80vh] flex items-center justify-center text-9xl">
                              🎉
                            </div>
                          )}
                        </div>
                        <div className="mt-2 font-medium text-green-700">
                          Quiz completed successfully • {formatted}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="h-full p-4">
                      <div className="flex items-start w-full gap-3 p-4 text-green-800 border border-green-300 rounded-lg bg-green-50">
                        <Award className="w-6 h-6 text-green-700" />
                        <div>
                          <div className="font-semibold">
                            Certificate issued
                          </div>
                          <div className="text-sm">
                            Quiz completed on {formatted}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="space-y-4">
                    {questions.map((q: any, qi: number) => {
                      const questionId = q.id || q.QuestionId; // Handle different ID fields
                      return (
                        <div key={questionId} className="p-3 border rounded-md">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="text-sm font-medium">
                              {qi + 1}. {q.question || q.Question}
                            </div>
                            {result && (
                              <span
                                className={`text-xs px-1.5 py-0.5 rounded ${result.per[questionId]
                                    ? "bg-green-50 text-green-700 border border-green-200"
                                    : "bg-red-50 text-red-700 border border-red-200"
                                  }`}
                              >
                                {result.per[questionId]
                                  ? "Correct"
                                  : "Incorrect"}
                              </span>
                            )}
                          </div>
                          <div className="space-y-2">
                            {(q.options || q.Options || []).map(
                              (opt: string, oi: number) => (
                                <label
                                  key={oi}
                                  className="flex items-center gap-2 text-sm"
                                >
                                  <input
                                    type="radio"
                                    name={`q-${questionId}`}
                                    className="w-4 h-4"
                                    checked={answers[questionId] === oi}
                                    onChange={() =>
                                      setAnswersByDoc((prev) => ({
                                        ...prev,
                                        [docId]: {
                                          ...(prev[docId] || {}),
                                          [questionId]: oi,
                                        },
                                      }))
                                    }
                                    disabled={!!completedAt}
                                  />
                                  <span>{opt}</span>
                                </label>
                              )
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            ) : (
              <div className="text-sm text-gray-600">
                No quiz added for this document.
              </div>
            )}
          </div>
          {/* Quiz bottom bar - only show if quiz exists and hasn't been performed */}
          {hasQuiz && !isQuizPerformed
            ? (() => {
              const docId = activeDoc!.id;
              const answers = answersByDoc[docId] || {};

              // Get questions from any of the possible locations
              const questions = activeDoc!.quiz?.questions || [];

              const total = questions.length;
              const answeredCount = questions.filter((q: any) => {
                const questionId = q.id || q.QuestionId;
                return (
                  answers[questionId] !== undefined &&
                  answers[questionId] !== null
                );
              }).length;

              const completedAt = quizCompletedAt[docId];
              const result = quizResult[docId];
              const started = !!quizStarted[docId];
              if (!started && !completedAt) return null;
              const scoreText = result
                ? `Score ${result.score}/${result.total}`
                : `Answered ${answeredCount}/${total}`;
              if (completedAt) {
                const formatted = new Date(completedAt).toLocaleString(
                  "en-GB",
                  {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  }
                );
                return (
                  <div className="px-4 py-3 border-t border-green-200 bg-green-50">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        Quiz completed successfully • {formatted}
                      </span>
                    </div>
                  </div>
                );
              }
              return (
                <div className="px-4 py-3 bg-white border-t">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">{scoreText}</div>
                    {!result ? (
                      <Button
                        size="sm"
                        disabled={answeredCount !== total}
                        onClick={handleSubmitQuiz}
                      >
                        Submit Answers
                      </Button>
                    ) : result.score === total ? (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() =>
                            setQuizCompletedAt((p) => ({
                              ...p,
                              [docId]: new Date().toISOString(),
                            }))
                          }
                        >
                          Mark Quiz Complete
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setQuizResult((prev) => {
                              const cp = { ...prev };
                              delete cp[docId];
                              return cp;
                            });
                            setAnswersByDoc((prev) => ({
                              ...prev,
                              [docId]: {},
                            }));
                          }}
                        >
                          Try Again
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()
            : null}
        </div>
      </div>
    </div>
  );
}
